import 'keen-slider/keen-slider.min.css'

import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Arrow } from 'app/components/Common/Arrow'
import { styles } from 'app/theme'
import { useKeenSlider } from 'keen-slider/react'
import React, { memo, useEffect, useState } from 'react'

import { Poi, Props as PoiProps } from './Poi'

export interface Props {
  items: PoiProps[]
  title?: string
}

export const PoiSlider = memo(function PoiSlider({ items, title }: Props) {
  if (!items) {
    return null
  }
  if (items.length < 1) {
    return null
  }

  const [currentSlide, setCurrentSlide] = useState(0)
  const [prevLoaded, setPrevLoaded] = useState<boolean[]>([])
  const [prevTwoLoaded, setPrevTwoLoaded] = useState<boolean[]>([])
  const [nextLoaded, setNextLoaded] = useState<boolean[]>([])
  const [nextTwoLoaded, setNextTwoLoaded] = useState<boolean[]>([])

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    drag: false,
    slides: {
      perView: 2.86,
      spacing: 84,
    },
    created(s) {
      setCurrentSlide(s.track.details.rel)
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
    },
    breakpoints: {
      '(max-width: 1199px)': {
        drag: true,
        slides: {
          perView: 1.3,
          spacing: 24,
        },
      },
    },
  })

  useEffect(() => {
    const newPrevTwoLoaded = [...prevTwoLoaded]
    newPrevTwoLoaded[
      Math.sign(currentSlide - 2) === 1 ? currentSlide - 2 : items.length - 2
    ] = true
    const newPrevLoaded = [...prevLoaded]
    newPrevLoaded[
      Math.sign(currentSlide - 1) === 1 ? currentSlide - 1 : items.length - 1
    ] = true
    const newNextLoaded = [...nextLoaded]
    newNextLoaded[currentSlide + 1] = true
    const newNextTwoLoaded = [...nextTwoLoaded]
    newNextTwoLoaded[currentSlide + 2] = true

    setPrevTwoLoaded(newPrevTwoLoaded)
    setPrevLoaded(newPrevLoaded)
    setNextLoaded(newNextLoaded)
    setNextTwoLoaded(newNextTwoLoaded)
  }, [currentSlide, instanceRef, sliderRef])

  return (
    <Container>
      {title ? <Title text={title} length={80} /> : null}

      <FadeInUp>
        <Wrapper className="item-wrapper">
          <Slider ref={sliderRef} className="keen-slider">
            {items.map((item, index) => (
              <Poi
                className="keen-slider__slide"
                key={index}
                isLazy={
                  prevTwoLoaded[index] ||
                  prevLoaded[index] ||
                  nextLoaded[index] ||
                  nextTwoLoaded[index]
                }
                {...item}
              />
            ))}
          </Slider>

          {items.length > 1 && (
            <Arrows>
              <Arrow
                onClick={(e) =>
                  e.stopPropagation() || instanceRef.current?.prev()
                }
              />

              <Arrow
                direction="R"
                onClick={(e) =>
                  e.stopPropagation() || instanceRef.current?.next()
                }
              />
            </Arrows>
          )}
        </Wrapper>
      </FadeInUp>
    </Container>
  )
})

const Container = styled.section`
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  margin: 0 auto;
  padding: 9rem 0 0;
  text-align: center;

  @media (max-width: 1199px) {
    padding: 3.75rem 0;
  }

  .item-wrapper {
    cursor: default;

    @media (max-width: 1199px) {
      cursor: grab;
    }
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.normal}

  margin-bottom: 3rem;
  text-align: left;
  padding-left: 10.139vw;

  @media (max-width: 1199px) {
    margin-bottom: 2rem;
    padding: 0 3rem;
  }
`

const Wrapper = styled.div`
  position: relative;

  cursor: none;
  padding-left: 10.139vw;

  @media (max-width: 1199px) {
    padding-left: 3rem;
  }
`

const Slider = styled.div`
  .keen-slider__slide {
    aspect-ratio: 0.75;
  }

  .keen-slider__slide {
    &:hover {
      .item-wrap {
        transform: translateY(0);
      }
      .item-bottom {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 1199px) {
      .item-wrap {
        transform: translateY(0);
      }
      .item-bottom {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`

const Arrows = styled.div`
  > div {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;
    &:first-of-type {
      left: 7.986vw;
    }
    &:last-of-type {
      right: 11.528vw;
    }

    svg {
      height: 0.875rem;
      fill: ${({ theme }) => theme.colors.variants.primaryDark};
    }
  }

  @media (max-width: 1199px) {
    display: none;
  }
`
